/* FAB — breathing dot ground.
 *
 * TWO HOOKS as of 2026-09-08, and it used to be four. `body.fab-password` is
 * the pre-launch page; `body.fab-ground` is every page of the site that earns
 * the ground, and WHICH pages those are is decided once in layout/theme.liquid.
 * This file used to name them — fab-catalog, fab-home, fab-page — which meant
 * the list lived here as well as there, and on 2026-08-28 a hook was added to
 * the layout and not to this line: the homepage carried the class, painted the
 * static lattice, and never breathed for two days with nothing erroring. That
 * copy is now gone rather than corrected.
 *
 * The two hosts reuse one ground verbatim — same period, same radius, same
 * alpha band. Each supplies its own static reduced-motion lattice from its own
 * stylesheet: fab-bridge.css for the password page, fab-catalog.css for the
 * site.
 *
 * ⚠️ THE GROUND DOES NOT SCROLL (Tioma, 2026-08-30). The canvas is sized to the
 * VIEWPORT and positioned `fixed` by both stylesheets; the content moves over
 * it. It used to be document-sized and absolute, which made it a texture on the
 * page rather than a surface under it.
 *
 * Replaces the static micro-dot-grid (brand-fab §5.7: 20px period, 1.2px
 * dots, 10px offset) with a canvas where every dot breathes on its own
 * smooth sine cycle — random phase, random 4–8s period — so the page reads
 * as alive rather than blinking. Tioma, 2026-08-20.
 *
 * Opacity-only (Call 4 Tier 1). Ink is iron-400 at a low alpha band whose
 * midpoint matches the static grid's iron-200 ink, so the animated and
 * static grounds read as the same surface. 30fps — breathing needs no more.
 *
 * prefers-reduced-motion: this file draws nothing; the host's static CSS grid
 * remains (the §23 contract collapses motion, not content). Same when JS is
 * unavailable. Flipping the preference mid-session is honoured live in both
 * directions.
 *
 * FAB-owned file — not upstream Horizon. Safe under `git fetch upstream`.
 */
(() => {
  const body = document.body;
  if (!body.classList.contains('fab-password') && !body.classList.contains('fab-ground')) return;

  /* THE MOUNT IS THE ELEMENT THAT ACTUALLY SCROLLS, and the two layouts do not
     agree on which one that is.

     layout/password.liquid has no wrapper: html and body scroll, and the page
     is one viewport tall anyway. layout/theme.liquid does NOT — measured on
     the live catalog page: html and body are both `overflow: hidden` at
     exactly viewport height, and `.page-wrapper` carries `overflow: auto`. So
     on a theme.liquid page `body.scrollHeight` is the VIEWPORT height, not the
     document height, forever.

     Left mounted on the body, this canvas therefore sized itself to one
     viewport on a ~1920px-tall catalog and then sat still while the content
     scrolled past it — dots on the first screen and bare ground below.
     Measured before this fix: bodyScrollH 1000 against a last cell whose
     bottom was 1922. Mounting into the real scroller fixes both the height and
     the scrolling in one move, and changes nothing on the password page, where
     this resolves to the body exactly as before. */
  const mount = document.querySelector('.page-wrapper') || body;

  const mq = matchMedia('(prefers-reduced-motion: reduce)');
  const GRID = 20;
  const OFFSET = 10;
  const RADIUS = 1.2;
  const FRAME_MS = 33; /* ~30fps */
  const ALPHA_MID = 0.45; /* iron-400 at 0.45 ≈ the static iron-200 ink */
  const ALPHA_AMP = 0.3;

  let canvas = null;
  let raf = 0;
  let dots = [];
  let ctx, sprite, width, height, last = 0;

  function buildSprite(ink) {
    const dpr = window.devicePixelRatio || 1;
    const size = Math.ceil(RADIUS * 2 * dpr);
    sprite = document.createElement('canvas');
    sprite.width = size;
    sprite.height = size;
    const sctx = sprite.getContext('2d');
    sctx.fillStyle = ink;
    sctx.beginPath();
    sctx.arc(size / 2, size / 2, RADIUS * dpr, 0, Math.PI * 2);
    sctx.fill();
  }

  /* ⚠️ THE CANVAS IS THE VIEWPORT, NOT THE DOCUMENT — changed 2026-08-30 on
     Tioma's call: *"they should be set in place without moving. Only the
     content of the page moves; the background stays in place."*

     It used to size itself to `mount.scrollWidth/scrollHeight` and sit
     `position: absolute` inside the scroller, so the lattice was as tall as the
     document and travelled with it. A ground that scrolls is a texture ON the
     content; a ground that holds still is a surface the content moves over,
     which is what the brand's dotted ground is meant to be.

     Sizing to the viewport is not just a positioning change, it is most of the
     cost gone: the catalog drew a canvas several thousand pixels tall and
     breathed every dot in it, including the ~90% never on screen. One viewport
     is the only part anyone can see. */
  function layout() {
    const dpr = window.devicePixelRatio || 1;
    width = innerWidth;
    height = innerHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    dots = [];
    for (let x = OFFSET; x < width; x += GRID) {
      for (let y = OFFSET; y < height; y += GRID) {
        dots.push({
          x,
          y,
          phase: Math.random() * Math.PI * 2,
          period: 4000 + Math.random() * 4000,
        });
      }
    }
  }

  function frame(t) {
    raf = requestAnimationFrame(frame);
    if (t - last < FRAME_MS) return;
    last = t;
    ctx.clearRect(0, 0, width, height);
    const s = RADIUS * 2;
    for (const d of dots) {
      const a = ALPHA_MID + ALPHA_AMP * Math.sin(d.phase + (t / d.period) * Math.PI * 2);
      ctx.globalAlpha = a;
      ctx.drawImage(sprite, d.x - RADIUS, d.y - RADIUS, s, s);
    }
  }

  function start() {
    if (canvas) return;
    const ink = getComputedStyle(body).getPropertyValue('--color-iron-400').trim() || '#9EA2A9';
    canvas = document.createElement('canvas');
    canvas.className = 'fab-breathing-dots';
    canvas.setAttribute('aria-hidden', 'true');
    mount.prepend(canvas);
    body.classList.add('fab-dots-animated');
    buildSprite(ink);
    layout();
    raf = requestAnimationFrame(frame);
    addEventListener('resize', layout);

    /* ⚠️ THE ResizeObserver IS GONE, AND ITS REASON WENT WITH IT. It existed
       because the canvas was sized to the DOCUMENT, whose height only settles
       once the product images have laid out — a canvas sized before that
       covered the top of the catalog and left the rest bare. A viewport-sized
       canvas has no such dependency: the viewport is known at first paint and
       changes only on resize, which `resize` already reports. Keeping the
       observer would mean re-laying out the whole lattice every time a product
       image finished loading, for no visible change. */
  }

  function stop() {
    if (!canvas) return;
    cancelAnimationFrame(raf);
    removeEventListener('resize', layout);
    canvas.remove();
    canvas = null;
    body.classList.remove('fab-dots-animated');
  }

  mq.addEventListener('change', () => (mq.matches ? stop() : start()));
  if (!mq.matches) start();
})();
